// Planningsregels die server en web delen: datums, deadlinestatus, categorie uit labels, en het toepassen van
// wijzigingen op een taak (zo ziet de gebruiker een wijziging meteen, ook al duurt Planner 15 s tot 2 min).
import type { Me, PlanningData, Task, TaskOp } from "./types";

const DAY = 864e5;
const D = (s: string) => new Date(s.slice(0, 10) + "T00:00:00Z");

// Links naar Planner zelf: wie geen Project-licentie heeft, wijzigt daar (basisbewerking in premiumplannen).
export const plannerTaskUrl = (me: Me, t: { id: string; projectId: string }) => me.plannerTaskUrl.replace("{projectId}", t.projectId).replace("{taskId}", t.id);
export const plannerPlanUrl = (me: Me, projectId: string | null) => (projectId ? me.plannerPlanUrl.replace("{projectId}", projectId) : "https://planner.cloud.microsoft/");

export function todayIn(tz = "Europe/Brussels", at = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
}
export const addDays = (s: string, n: number) => new Date(D(s).getTime() + n * DAY).toISOString().slice(0, 10);
/** b - a in dagen */
export const diffDays = (a: string, b: string) => Math.round((D(b).getTime() - D(a).getTime()) / DAY);
/** 0 = maandag */
export const dow = (s: string) => (D(s).getUTCDay() + 6) % 7;
export const mondayOf = (s: string) => addDays(s, -dow(s));
export const ddmm = (s: string) => { const [, m, d] = s.slice(0, 10).split("-"); return `${d}/${m}`; };
export const dmy = (s: string) => { const [y, m, d] = s.slice(0, 10).split("-"); return `${d}/${m}/${y}`; };
export function isoWeek(s: string): number {
  const t = D(s);
  t.setUTCDate(t.getUTCDate() + 3 - ((t.getUTCDay() + 6) % 7));
  const w1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((t.getTime() - w1.getTime()) / DAY - 3 + ((w1.getUTCDay() + 6) % 7)) / 7);
}

export type DeadlineKey = "verlopen" | "telaat" | "vandaag" | "binnenkort" | "gepland";
export interface Deadline { key: DeadlineKey; tone: "" | "warn" | "err"; text: string; long: string; od: number }

/** Recent te laat (1 tot 30 dagen) is rood; langer geleden is "verlopen" en hoort in Opschonen. Drempels uit Instellingen. */
export function deadline(end: string, today: string, lateDays = rules.lateDays, soonDays = rules.soonDays): Deadline {
  const od = diffDays(end, today);
  if (od > lateDays) return { key: "verlopen", tone: "warn", text: "verlopen " + ddmm(end), long: "Deadline " + ddmm(end) + " verlopen", od };
  if (od >= 1) return { key: "telaat", tone: "err", text: od + " d te laat", long: od + (od === 1 ? " dag" : " dagen") + " te laat (" + ddmm(end) + ")", od };
  if (od === 0) return { key: "vandaag", tone: "err", text: "vandaag", long: "Deadline vandaag", od };
  if (od >= -soonDays) return { key: "binnenkort", tone: "warn", text: ddmm(end) + " · over " + -od + " d", long: "Deadline " + ddmm(end) + ", over " + -od + " dagen", od };
  return { key: "gepland", tone: "", text: ddmm(end), long: "Deadline " + ddmm(end), od };
}
export const URGENCY: Record<DeadlineKey, number> = { vandaag: 0, binnenkort: 1, telaat: 2, gepland: 3, verlopen: 4 };

export type Category = "actief" | "geparkeerd" | "afsluiten";
/** Betekenis van een Planner-label; instelbaar in Instellingen. */
export type LabelMeaning = "actief" | "geparkeerd" | "afsluiten" | "negeren";
export const DEFAULT_LABELS: Record<string, LabelMeaning> = {
  "to do": "actief", ongoing: "actief", test: "actief", "test validated": "afsluiten", prod: "afsluiten", waiting: "geparkeerd", "on hold": "geparkeerd",
};

/**
 * Regels uit Instellingen. lib/settings.ts zet ze na het laden met setRules; tot dan gelden de standaardwaarden.
 * Zo gebruiken deadline, categoryOf en catOf de instellingen zonder dat hun aanroepen veranderen.
 */
export interface Rules { soonDays: number; lateDays: number; labels: Record<string, LabelMeaning> }
const rules: Rules = { soonDays: 7, lateDays: 30, labels: DEFAULT_LABELS };
export function setRules(r: Partial<Rules>) {
  Object.assign(rules, r, r.labels ? { labels: { ...DEFAULT_LABELS, ...r.labels } } : {});
}
export const getRules = (): Readonly<Rules> => rules;
/** Betekenis van één label; een onbekend label is actief werk. */
export const meaningOf = (name: string): LabelMeaning => rules.labels[name.trim().toLowerCase()] || "actief";

export function categoryOf(labels: { name: string }[], meaning: Record<string, LabelMeaning> = rules.labels): Category {
  const m = labels.map((l) => meaning[l.name.trim().toLowerCase()]);
  if (m.includes("afsluiten")) return "afsluiten";
  if (m.includes("geparkeerd")) return "geparkeerd";
  return "actief";
}

export const PRIORITIES: [number, string][] = [[1, "Dringend"], [3, "Belangrijk"], [5, "Normaal"], [9, "Laag"]];
export const priorityLabel = (p: number) => (PRIORITIES.find((x) => x[0] === p) || PRIORITIES[2])[1];

export const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
export const firstName = (n: string) => n.split(/\s+/)[0];

/**
 * Past wijzigingen toe op een kopie van de taak, met de gegevens die nodig zijn om namen en teamleden op te zoeken.
 * Nieuwe koppelingen krijgen een tijdelijke id ("pending:...") tot Planner de echte teruggeeft.
 */
export function applyOps(task: Task, ops: TaskOp[], data: Pick<PlanningData, "labels" | "team"> & Partial<Pick<PlanningData, "fases">>): Task {
  const t: Task = { ...task, labels: [...task.labels], owners: [...task.owners], checklist: task.checklist.map((c) => ({ ...c })), attachments: [...task.attachments] };
  for (const op of ops) {
    switch (op.kind) {
      case "dates":
        if (op.start) t.start = op.start;
        if (op.end) t.end = op.end;
        break;
      case "progress": t.progress = op.progress; break;
      case "bucket": t.bucketId = op.bucketId; break;
      case "priority": t.priority = op.priority; break;
      case "notes": t.notes = op.text; break;
      case "label": {
        const has = t.labels.some((l) => l.id === op.labelId);
        if (op.on && !has) {
          const def = data.labels.find((l) => l.id === op.labelId);
          if (def) t.labels.push({ id: def.id, name: def.name, linkId: "pending:" + def.id });
        }
        if (!op.on) t.labels = t.labels.filter((l) => l.id !== op.labelId);
        break;
      }
      case "owner": {
        const has = t.owners.some((o) => o.personId === op.personId);
        if (op.on && !has) {
          const tm = data.team.find((m) => m.projectId === t.projectId && m.personId === op.personId);
          t.owners.push({ personId: op.personId, assignmentId: "pending:" + op.personId, resourceId: tm ? tm.resourceId : "" });
        }
        if (!op.on) t.owners = t.owners.filter((o) => o.personId !== op.personId);
        break;
      }
      case "checklist": t.checklist = t.checklist.map((c) => (c.id === op.itemId ? { ...c, done: op.done } : c)); break;
      case "link":
        if (op.on && !t.attachments.some((a) => a.uri === op.url)) t.attachments.push({ id: "pending:" + op.url, name: op.name || op.url, type: op.type || "Other", uri: op.url });
        if (!op.on) t.attachments = t.attachments.filter((a) => a.uri !== op.url);
        break;
      case "dep": break; // afhankelijkheden staan niet op de taak zelf maar in data.deps (outbox.applyDeps)
      case "title": t.title = op.text.trim(); break;
      case "checklist-add":
        if (!t.checklist.some((c) => c.id === op.itemId)) t.checklist.push({ id: op.itemId, name: op.name, done: op.done });
        break;
      case "checklist-remove": t.checklist = t.checklist.filter((c) => c.id !== op.itemId); break;
      case "checklist-rename": t.checklist = t.checklist.map((c) => (c.id === op.itemId ? { ...c, name: op.name } : c)); break;
      case "milestone": t.milestone = op.on; break;
      case "parent": {
        t.faseId = op.faseId;
        const f = op.faseId ? data.fases?.find((x) => x.id === op.faseId) : undefined;
        if (f?.bucketId) t.bucketId = f.bucketId; // een subtaak staat in Planner in de bucket van haar fase
        break;
      }
    }
  }
  t.labels.sort((a, b) => a.name.localeCompare(b.name));
  return t;
}

/** De tegenzet van een reeks wijzigingen, voor Ongedaan maken. */
export function inverseOps(before: Task, ops: TaskOp[]): TaskOp[] {
  const inv: TaskOp[] = [];
  for (const op of ops) {
    switch (op.kind) {
      case "dates": inv.push({ kind: "dates", start: op.start ? before.start : undefined, end: op.end ? before.end : undefined }); break;
      case "progress": inv.push({ kind: "progress", progress: before.progress }); break;
      case "bucket": inv.push({ kind: "bucket", bucketId: before.bucketId }); break;
      case "priority": inv.push({ kind: "priority", priority: before.priority }); break;
      case "notes": inv.push({ kind: "notes", text: before.notes }); break;
      case "label": inv.push({ kind: "label", labelId: op.labelId, on: before.labels.some((l) => l.id === op.labelId) }); break;
      case "owner": inv.push({ kind: "owner", personId: op.personId, on: before.owners.some((o) => o.personId === op.personId) }); break;
      case "checklist": { const c = before.checklist.find((x) => x.id === op.itemId); if (c) inv.push({ kind: "checklist", itemId: op.itemId, done: c.done }); break; }
      case "dep": inv.push({ kind: "dep", pred: op.pred, on: !op.on }); break;
      case "link": inv.push({ ...op, on: !op.on }); break;
      case "title": inv.push({ kind: "title", text: before.title }); break;
      case "checklist-add": inv.push({ kind: "checklist-remove", itemId: op.itemId }); break;
      case "checklist-remove": { const c = before.checklist.find((x) => x.id === op.itemId); if (c) inv.push({ kind: "checklist-add", itemId: c.id, name: c.name, done: c.done }); break; }
      case "checklist-rename": { const c = before.checklist.find((x) => x.id === op.itemId); if (c) inv.push({ kind: "checklist-rename", itemId: c.id, name: c.name }); break; }
      case "milestone": inv.push({ kind: "milestone", on: before.milestone }); break;
      case "parent": inv.push({ kind: "parent", faseId: before.faseId }); break;
    }
  }
  return inv.reverse();
}
